/**
 * Tasks 1.0 pure model: statuses, validation, catalog → Project Task snapshot,
 * ordering, grouping and filtering. No data access.
 *
 * TASK CATALOG item  = reusable work definition (title + default instructions).
 * PROJECT TASK       = an actual piece of work on one project (optionally one
 *                      location). It is an independent SNAPSHOT: title and
 *                      description are copied from the catalog item, and only
 *                      `source_task_catalog_id` is kept as provenance.
 */

import { formatDuration, isValidDurationMinutes, normalizeProgress } from "./taskDuration";

export const TASK_STATUS = Object.freeze({ OPEN: "open", COMPLETED: "completed" });
export const TASK_PRIORITY = Object.freeze({ LOW: "low", MEDIUM: "medium", HIGH: "high" });
export const PRIORITY_OPTIONS = [
  { value: "", label: "None" },
  { value: TASK_PRIORITY.LOW, label: "Low" },
  { value: TASK_PRIORITY.MEDIUM, label: "Medium" },
  { value: TASK_PRIORITY.HIGH, label: "High" },
];
export const GENERAL_LOCATION = "__general__";
export const TITLE_MAX = 200;
export const DESCRIPTION_MAX = 2000;

export function normalizeTitle(value) {
  return String(value ?? "").replace(/\s+/g, " ").trim();
}

/** Case / spacing-insensitive identity used by the duplicate guard. */
export function titleKey(value) {
  return normalizeTitle(value).toLowerCase();
}

export function priorityLabel(priority) {
  return PRIORITY_OPTIONS.find((option) => option.value === (priority || ""))?.label || "";
}

export function validateTaskInput({
  title,
  description = "",
  priority = "",
  dueDate = "",
  startDate = "",
  durationMinutes = null,
} = {}) {
  const errors = {};
  const cleanTitle = normalizeTitle(title);
  if (!cleanTitle) errors.title = "Enter a task title.";
  else if (cleanTitle.length > TITLE_MAX) errors.title = `Keep the title under ${TITLE_MAX} characters.`;
  if (String(description || "").length > DESCRIPTION_MAX) {
    errors.description = `Keep the description under ${DESCRIPTION_MAX} characters.`;
  }
  if (priority && !Object.values(TASK_PRIORITY).includes(priority)) {
    errors.priority = "Choose Low, Medium or High.";
  }
  if (dueDate && !/^\d{4}-\d{2}-\d{2}$/.test(dueDate)) {
    errors.dueDate = "Enter a valid due date.";
  }
  if (startDate && !/^\d{4}-\d{2}-\d{2}$/.test(startDate)) {
    errors.startDate = "Enter a valid start date.";
  } else if (startDate && dueDate && !errors.dueDate && startDate > dueDate) {
    errors.startDate = "Start date must be on or before the due date.";
  }
  if (durationMinutes != null && !isValidDurationMinutes(durationMinutes)) {
    errors.duration = "Enter a planned duration greater than zero.";
  }
  return { ok: Object.keys(errors).length === 0, errors };
}

/** The catalog row that already uses this title (archived rows included), or null. */
export function findCatalogDuplicate(catalog = [], title, { excludeId = null } = {}) {
  const key = titleKey(title);
  if (!key) return null;
  return (
    catalog.find(
      (item) => item.task_catalog_id !== excludeId && titleKey(item.title) === key,
    ) || null
  );
}

/**
 * Catalog item → Project Task payload. Copies the text; the result never reads
 * the catalog again, and editing it never touches the catalog.
 */
export function buildTaskFromCatalog(
  item,
  { projectId, locationId = null, featureId = null, title, description, ...extra } = {},
) {
  // A catalog item (template) carries a planned duration only, never dates.
  // An explicit duration in `extra` (the editor) wins; undefined = template's.
  const durationMinutes =
    extra.durationMinutes !== undefined ? extra.durationMinutes : item.planned_duration_minutes ?? null;
  // The user may tweak the copied text before saving; those edits stay on the
  // project task (the catalog item is never written).
  const text = title !== undefined ? title : item.title;
  const detail = description !== undefined ? description : item.description;
  return {
    project_id: projectId,
    location_id: locationId || null,
    ...(locationId && featureId ? { feature_id: featureId } : {}),
    title: normalizeTitle(text),
    description: String(detail || "").trim() || null,
    source_task_catalog_id: item.task_catalog_id || null,
    ...pickOptional({ ...extra, durationMinutes }),
  };
}

export function buildManualTask({ projectId, locationId = null, featureId = null, title, description = "", ...extra }) {
  return {
    project_id: projectId,
    location_id: locationId || null,
    ...(locationId && featureId ? { feature_id: featureId } : {}),
    title: normalizeTitle(title),
    description: String(description || "").trim() || null,
    source_task_catalog_id: null,
    ...pickOptional(extra),
  };
}

/**
 * Task scope. A Task lives at its most specific level:
 *   Project task  : no location, no feature
 *   Location task : location, no feature
 *   Feature task  : location + feature (the feature belongs to that location)
 * Returns the safe pair: a feature without a location, or one that is not a
 * feature of that location, is dropped (the UI clears it the same way).
 * `locations` is the nested structure ({ project_location_id, features: [...] }).
 */
export function resolveTaskScope(locationId, featureId, locations = []) {
  const location = locationId
    ? locations.find((row) => row.project_location_id === locationId)
    : null;
  if (!locationId) return { locationId: "", featureId: "" };
  const belongs =
    featureId && (location?.features || []).some((f) => f.project_feature_id === featureId);
  return { locationId, featureId: belongs ? featureId : "" };
}

/** Features offered for a location (never another location's). */
export function featuresForLocation(locationId, locations = []) {
  if (!locationId) return [];
  return (
    locations.find((row) => row.project_location_id === locationId)?.features || []
  );
}

/** "Project", "Project › Location", "Project › Location › Feature" (no empty separators). */
export function taskPathLabel({ projectName, locationName, featureName } = {}) {
  return [projectName, locationName, featureName].filter(Boolean).join(" › ");
}

function pickOptional({ priority, assigneeId, dueDate, startDate, durationMinutes } = {}) {
  return {
    priority: priority || null,
    assignee_id: assigneeId || null,
    due_date: dueDate || null,
    // Schedule columns are only sent when set, so a task without them never
    // depends on the 20261021200000 columns (and nothing is fabricated).
    ...(startDate ? { start_date: startDate } : {}),
    ...(durationMinutes ? { planned_duration_minutes: durationMinutes } : {}),
  };
}

/** Deterministic order: sort_order, then created_at, then id. */
export function sortTasks(tasks = []) {
  return [...tasks].sort(
    (a, b) =>
      (Number(a.sort_order) || 0) - (Number(b.sort_order) || 0) ||
      String(a.created_at || "").localeCompare(String(b.created_at || "")) ||
      String(a.project_task_id).localeCompare(String(b.project_task_id)),
  );
}

export function countTasks(tasks = []) {
  const open = tasks.filter((task) => task.status !== TASK_STATUS.COMPLETED).length;
  return { open, completed: tasks.length - open, total: tasks.length };
}

export function taskCountLabel(tasks = []) {
  const { open, completed, total } = countTasks(tasks);
  if (!total) return "No tasks yet";
  return `${open} open · ${completed} completed`;
}

export function filterTasks(tasks = [], { status = "all", locationId = "", featureId = "", search = "" } = {}) {
  const needle = String(search || "").trim().toLowerCase();
  return tasks.filter((task) => {
    if (status === TASK_STATUS.OPEN && task.status === TASK_STATUS.COMPLETED) return false;
    if (status === TASK_STATUS.COMPLETED && task.status !== TASK_STATUS.COMPLETED) return false;
    if (locationId === GENERAL_LOCATION && task.location_id) return false;
    if (locationId && locationId !== GENERAL_LOCATION && task.location_id !== locationId) return false;
    // Feature narrows within a Location (callers only pass one that belongs to it).
    if (featureId && task.feature_id !== featureId) return false;
    if (needle) {
      const hay = `${task.title} ${task.description || ""}`.toLowerCase();
      if (!hay.includes(needle)) return false;
    }
    return true;
  });
}

/**
 * Feature filter options for the Location being shown: that Location's active
 * Features only (canonical project_features under project_location_id). None
 * for All Locations / General, so a Feature is never offered across Locations.
 */
export function featureFilterOptions(locations = [], locationId = "") {
  if (!locationId || locationId === GENERAL_LOCATION) return [];
  const location = locations.find((l) => l.project_location_id === locationId);
  return (location?.features || [])
    .filter((f) => f && f.project_feature_id && f.is_active !== false)
    .map((f) => ({ id: f.project_feature_id, name: f.name || "Feature" }));
}

/** Location groups in the project's location order; project-level tasks last as General. */
export function groupTasksByLocation(tasks = [], locations = []) {
  const ordered = sortTasks(tasks);
  const groups = [];
  const known = new Set();
  locations.forEach((location) => {
    known.add(location.project_location_id);
    const rows = ordered.filter((task) => task.location_id === location.project_location_id);
    if (rows.length) {
      groups.push({ locationId: location.project_location_id, label: location.name, tasks: rows });
    }
  });
  // Tasks whose location is no longer listed (archived location) keep their row.
  const orphan = ordered.filter((task) => task.location_id && !known.has(task.location_id));
  if (orphan.length) groups.push({ locationId: "__orphan__", label: "Other location", tasks: orphan });
  const general = ordered.filter((task) => !task.location_id);
  if (general.length) groups.push({ locationId: null, label: "General", tasks: general });
  return groups.map((group) => ({ ...group, ...countTasks(group.tasks) }));
}

export function dueState(task, todayIso) {
  if (!task?.due_date || task.status === TASK_STATUS.COMPLETED) return null;
  if (task.due_date < todayIso) return "overdue";
  if (task.due_date === todayIso) return "today";
  return null;
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export function formatTaskDate(value, { year = false } = {}) {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value || ""));
  if (!match) return "";
  const label = `${MONTHS[Number(match[2]) - 1]} ${Number(match[3])}`;
  return year ? `${label}, ${match[1]}` : label;
}

/** "Completed · Sep 28" or "High · Due Oct 5". */
export function taskMetaLabel(task, { minutesPerDay = null } = {}) {
  if (task.status === TASK_STATUS.COMPLETED) {
    const day = formatTaskDate(String(task.completed_at || "").slice(0, 10));
    return day ? `Completed · ${day}` : "Completed";
  }
  const parts = [];
  if (task.priority) parts.push(priorityLabel(task.priority));
  if (task.due_date) parts.push(`Due ${formatTaskDate(task.due_date)}`);
  const duration = formatDuration(task.planned_duration_minutes, minutesPerDay);
  if (duration) parts.push(duration);
  const progress = normalizeProgress(task.progress_percent);
  if (progress != null && !Number.isNaN(progress)) parts.push(`${progress}%`);
  return parts.join(" · ");
}

/**
 * Move one task up/down among the tasks that share its location. Returns the
 * [{ project_task_id, sort_order }] updates to persist (empty when it cannot
 * move). Orders are renumbered 1..n inside the project so equal or gappy
 * values never produce an ambiguous sequence.
 */
export function planTaskMove(tasks = [], taskId, direction) {
  const ordered = sortTasks(tasks);
  const target = ordered.find((task) => task.project_task_id === taskId);
  if (!target) return [];
  const siblings = ordered.filter((task) => (task.location_id || null) === (target.location_id || null));
  const index = siblings.findIndex((task) => task.project_task_id === taskId);
  const swapWith = siblings[direction === "up" ? index - 1 : index + 1];
  if (!swapWith) return [];
  const sequence = ordered.map((task) => task.project_task_id);
  const a = sequence.indexOf(taskId);
  const b = sequence.indexOf(swapWith.project_task_id);
  [sequence[a], sequence[b]] = [sequence[b], sequence[a]];
  return sequence
    .map((id, position) => ({ project_task_id: id, sort_order: position + 1 }))
    .filter((next) => {
      const current = ordered.find((task) => task.project_task_id === next.project_task_id);
      return Number(current.sort_order) !== next.sort_order;
    });
}
